import cx from 'classnames';
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import MapboxGL from 'mapbox-gl';
import type { AnySourceData, LngLatBoundsLike } from 'mapbox-gl';
import type { ReactElement } from 'react';
import { Component, createRef } from 'react';
import type { MarkerProps } from '../Map/Marker';
import type { Popup } from './addMarker';
import { addMarker } from './addMarker';
import styles from './styles.module.css';

// need this variable because of babel-plugin-minify-replace : typeof 'node' extends 'node' is not valid !
const target = __TARGET__;

let mapboxglPromise: Promise<typeof MapboxGL> | undefined;

const loadMapboxGL = (): Promise<typeof MapboxGL> => {
  return import(/* webpackPreload: true */ /* webpackChunkName: 'mapbox-gl' */ 'mapbox-gl').then((res) => {
    const mapboxgl = res.default || res;
    mapboxgl.accessToken = process.env.MAP_BOX_API_KEY || '';
    return mapboxgl;
  });
};

const loadMapboxglPromiseLike: PromiseLike<typeof MapboxGL> = {
  // eslint-disable-next-line unicorn/no-thenable
  then(onfulfilled, onrejected) {
    if (!mapboxglPromise) {
      mapboxglPromise = loadMapboxGL();
    }
    return mapboxglPromise.then(onfulfilled).catch(onrejected);
  },
};

export interface Marker {
  lat: number;
  lng: number;
  popup?: Popup;
  type?: MarkerProps['type'];
}

const pinPadding = 150;

interface Point {
  lat: number;
  lng: number;
}

interface Layer {
  sourceName: string;
  source: AnySourceData;
  layers: any[];
}

export interface InteractiveMapProps {
  standalone?: boolean;
  markers?: Marker[];
  zoom?: number;
  forcedCenter?: Point;
  layers?: Layer[];
  onLoad?: (map: mapboxgl.Map) => void;
  containerProps?: React.HTMLAttributes<HTMLDivElement>;
  renderWhenFailedToLoadMapbox: () => ReactElement | null;
}

interface InteractiveMapState {
  failedToLoadMapbox?: boolean;
}

export class InteractiveMap extends Component<InteractiveMapProps, InteractiveMapState> {
  static defaultProps = {
    zoom: 8,
  };

  static mapboxglPromise = loadMapboxglPromiseLike;

  mapContainerRef = createRef<HTMLDivElement>();

  map: MapboxGL.Map | undefined = undefined;

  state = {
    failedToLoadMapbox: false,
  };

  componentDidMount(): void {
    if (__TARGET__ !== 'node') {
      loadMapboxglPromiseLike.then(
        (mapboxgl) => {
          const { markers, zoom } = this.props;

          const bounds: LngLatBoundsLike | undefined = markers?.reduce(
            (prevBound, marker) =>
              prevBound.extend(
                new mapboxgl.LngLatBounds({ lat: marker.lat, lng: marker.lng }, { lat: marker.lat, lng: marker.lng }),
              ),
            new mapboxgl.LngLatBounds(
              { lat: markers[0].lat, lng: markers[0].lng },
              { lat: markers[0].lat, lng: markers[0].lng },
            ),
          );

          // prevent error on promise resolve after InteractiveMap component is unmounted
          if (!this.mapContainerRef.current) return;

          const map = new mapboxgl.Map({
            container: this.mapContainerRef.current,
            style: 'mapbox://styles/ornikar/cjlf9rdcm8zbt2sm2br1s4fo7',
            center: this.props.forcedCenter || bounds?.getCenter(),
            zoom,
          });
          this.map = map;

          if (markers && markers.length > 1 && !this.props.forcedCenter && bounds) {
            map.fitBounds(bounds, { padding: pinPadding, duration: 0 });
          }

          map.on('load', () => {
            if (this.props.onLoad) {
              this.props.onLoad(map);
            }

            map.addControl(new mapboxgl.NavigationControl(), 'top-left');

            if (markers) {
              markers.forEach(({ lat, lng, type, popup }) => {
                addMarker?.(mapboxgl, map, lng, lat, type, popup);
              });
            }

            if (this.props.layers) {
              this.props.layers.forEach(({ sourceName, source, layers }) => {
                map.addSource(sourceName, source);
                layers.forEach((layer) => map.addLayer(layer));
              });
            }
          });
        },
        () => {
          this.setState({ failedToLoadMapbox: true });
        },
      );
    }
  }

  componentDidUpdate(prevProps: InteractiveMapProps): void {
    const mapInstance = this.map;

    // occurs with jest tests
    if (target === 'node') return;
    // ensure componentDidMount ended before calling update
    loadMapboxglPromiseLike.then(() => {
      // component was unmount
      if (!mapInstance) return;

      if (prevProps.forcedCenter !== this.props.forcedCenter && this.props.forcedCenter) {
        mapInstance.setCenter(this.props.forcedCenter);
      }

      if (prevProps.zoom !== this.props.zoom && this.props.zoom) {
        mapInstance.setZoom(this.props.zoom);
      }
    });
  }

  render(): ReactElement | null {
    if (this.state.failedToLoadMapbox) {
      return (
        <div {...this.props.containerProps}>
          {
            // prop is required in typescript but fallback for non typescript applications.
            this.props.renderWhenFailedToLoadMapbox ? this.props.renderWhenFailedToLoadMapbox() : null
          }
        </div>
      );
    }

    return (
      <div
        ref={this.mapContainerRef}
        data-chromatic={process.env.KITT_MAP_CHROMATIC_IGNORE === 'true' ? 'ignore' : undefined}
        {...this.props.containerProps}
        className={cx(styles.MapContainer, {
          [styles.Standalone]: this.props.standalone,
        })}
      />
    );
  }
}
